import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  FileText,
  Users,
  DollarSign,
  Calendar,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card } from '../../../components/ui/card';
import { useToast } from '../../../components/ui/use-toast';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SalesReport = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [clientId, setClientId] = useState('');
  
  // Report data
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Set default dates (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(format(today, 'yyyy-MM-dd'));
    setStartDate(format(thirtyDaysAgo, 'yyyy-MM-dd'));
  }, []);
  
  const fetchReport = async () => {
    if (!startDate || !endDate) {
      toast({
        variant: "destructive",
        title: "Missing Dates",
        description: "Please select start and end dates",
      });
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        currency: currency
      });
      
      if (clientId) {
        params.append('client_id', clientId);
      }
      
      const response = await axios.get(
        `${API_URL}/api/finance/reports/sales?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setReportData(response.data);
      
      toast({
        title: "Report Generated",
        description: "Sales report generated successfully",
      });
    } catch (error) {
      console.error('Error fetching sales report:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate report",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };
  
  const formatChartData = () => {
    if (!reportData?.revenue_by_month) return [];
    
    return reportData.revenue_by_month.map(item => ({
      month: item.month,
      revenue: parseFloat(item.revenue),
      invoices: item.invoice_count
    }));
  };
  
  const handleExport = async (format) => {
    if (!reportData) {
      toast({
        variant: "destructive",
        title: "No Report Data",
        description: "Please generate a report first",
      });
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        currency: currency
      });
      
      if (clientId) {
        params.append('client_id', clientId);
      }
      
      const response = await axios.get(
        `${API_URL}/api/finance/reports/sales/export/${format}?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const extension = format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `sales_report_${startDate}_${endDate}.${extension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Export Successful",
        description: `Sales report exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      console.error(`Error exporting to ${format}:`, error);
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: error.response?.data?.detail || `Failed to export report as ${format}`,
      });
    }
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#2B2D42] flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-[#00B4D8]" />
          Sales Report
        </h1>
        <p className="text-[#6C757D] mt-2">
          Comprehensive sales analysis and revenue metrics
        </p>
      </div>
      
      {/* Filters */}
      <Card className="p-6 mb-6 border-[#E9ECEF]">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-[#6C757D]" />
          <h2 className="text-lg font-semibold text-[#2B2D42]">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Start Date
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              End Date
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Currency
            </label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="BRL">BRL</SelectItem>
                <SelectItem value="JPY">JPY</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button
              onClick={fetchReport}
              disabled={loading}
              className="w-full bg-[#00B4D8] hover:bg-[#0096c7] text-white"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Generate Report
                </div>
              )}
            </Button>
          </div>
        </div>
      </Card>
      
      {/* Report Data */}
      {reportData && (
        <>
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Total Revenue */}
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Total Revenue</span>
                <DollarSign className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {formatCurrency(reportData.metrics.total_revenue)}
              </p>
              {reportData.metrics.growth_rate !== null && (
                <p className={`text-sm mt-2 flex items-center gap-1 ${
                  reportData.metrics.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  <TrendingUp className="w-4 h-4" />
                  {reportData.metrics.growth_rate >= 0 ? '+' : ''}
                  {reportData.metrics.growth_rate.toFixed(1)}% vs prev period
                </p>
              )}
            </Card>
            
            {/* Invoice Count */}
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Total Invoices</span>
                <FileText className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {reportData.metrics.invoice_count}
              </p>
              <p className="text-sm text-[#6C757D] mt-2">
                Paid invoices
              </p>
            </Card>
            
            {/* Average Invoice */}
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Average Value</span>
                <DollarSign className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {formatCurrency(reportData.metrics.average_invoice_value)}
              </p>
              <p className="text-sm text-[#6C757D] mt-2">
                Per invoice
              </p>
            </Card>
            
            {/* Period */}
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Period</span>
                <Calendar className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <p className="text-lg font-semibold text-[#2B2D42]">
                {format(new Date(reportData.metrics.period_start), 'MMM dd, yyyy')}
              </p>
              <p className="text-sm text-[#6C757D]">to</p>
              <p className="text-lg font-semibold text-[#2B2D42]">
                {format(new Date(reportData.metrics.period_end), 'MMM dd, yyyy')}
              </p>
            </Card>
          </div>
          
          {/* Revenue Chart */}
          <Card className="p-6 mb-6 border-[#E9ECEF]">
            <h3 className="text-lg font-semibold text-[#2B2D42] mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#00B4D8]" />
              Revenue Over Time
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={formatChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                <XAxis 
                  dataKey="month" 
                  stroke="#6C757D"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#6C757D"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `${currency} ${value.toLocaleString()}`}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E9ECEF',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#00B4D8" 
                  strokeWidth={2}
                  dot={{ fill: '#00B4D8', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Revenue"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
          
          {/* Top Clients */}
          {reportData.top_clients.length > 0 && (
            <Card className="p-6 mb-6 border-[#E9ECEF]">
              <h3 className="text-lg font-semibold text-[#2B2D42] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#00B4D8]" />
                Top 5 Clients by Revenue
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E9ECEF]">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#2B2D42]">
                        Client
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-[#2B2D42]">
                        Revenue
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-[#2B2D42]">
                        Invoices
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-[#2B2D42]">
                        % of Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.top_clients.map((client, index) => (
                      <tr key={client.client_id} className="border-b border-[#E9ECEF] hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#00B4D8] text-white flex items-center justify-center font-semibold">
                              {index + 1}
                            </div>
                            <span className="font-medium text-[#2B2D42]">
                              {client.client_name}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-[#2B2D42]">
                          {formatCurrency(client.total_revenue)}
                        </td>
                        <td className="py-3 px-4 text-right text-[#6C757D]">
                          {client.invoice_count}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="px-2 py-1 bg-[#00B4D8]/10 text-[#00B4D8] rounded-full text-sm font-medium">
                            {client.percentage_of_total.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          
          {/* Export Actions */}
          <Card className="p-6 border-[#E9ECEF]">
            <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">
              Export Report
            </h3>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => handleExport('pdf')}
              >
                <Download className="w-4 h-4" />
                Export PDF
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => handleExport('excel')}
              >
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => handleExport('csv')}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </Card>
        </>
      )}
      
      {/* Empty State */}
      {!reportData && !loading && (
        <Card className="p-12 text-center border-[#E9ECEF]">
          <FileText className="w-16 h-16 text-[#6C757D] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#2B2D42] mb-2">
            No Report Generated
          </h3>
          <p className="text-[#6C757D] mb-6">
            Select dates and click "Generate Report" to view sales analytics
          </p>
        </Card>
      )}
    </div>
  );
};

export default SalesReport;
